import {
  PbCondition,
  PbGetReturnAmountResponse,
  PbReturnAmountRule
} from '@itsrever/rever-api'
import {
  ShippingConfiguration,
  toFrontendFunction,
  toFrontendValues
} from '@/hooks/use-available-shipping-rules'
import { Country, EU_ALIAS_CODE, EU_ALIAS_NAME } from '@/domain/countries'

export type ShippingRule = {
  params: { cost: string }
  returnOriginalShippingCosts: boolean
  conditions: ShippingCondition[]
}

export type ShippingCondition = {
  function: string
  comparison: string
  values: string[]
}

export function toFrontendShippingConfig(
  shippingConfig: PbGetReturnAmountResponse,
  countries: Country[],
  t: (key: string) => string
): ShippingRule[] {
  if (!shippingConfig.rules) return []

  const frontendShippingConfig: ShippingRule[] = shippingConfig.rules.map(
    (rule) => {
      const frontRule: ShippingRule = {
        params: {
          cost: (parseFloat(rule.params?.Cost ?? '0') / 100).toString()
        },
        returnOriginalShippingCosts:
          rule.return_original_shipping_costs ?? false,
        conditions:
          rule.when?.conditions?.map((condition) => {
            const funcName = condition.function ?? ''
            return {
              function: funcName,
              comparison: condition.comparison ?? '',
              values: toFrontendValues(
                funcName,
                condition.values ?? [],
                countries,
                t
              )
            }
          }) ?? []
      }
      return frontRule
    }
  )
  return frontendShippingConfig
}

export function toBackendShippingConfig(
  rules: ShippingRule[],
  availableConfiguration: ShippingConfiguration,
  countries: Country[],
  t: (key: string) => string,
  reasonKeys: string[]
): PbReturnAmountRule[] {
  return rules.map((r) => {
    const rule: PbReturnAmountRule = {
      name: availableConfiguration.name,
      params: toBackendParams(r.params),
      return_original_shipping_costs: r.returnOriginalShippingCosts,
      when: {
        conditions: toBackendConditions(
          r.conditions,
          countries,
          t,
          reasonKeys,
          availableConfiguration
        )
      }
    }
    return rule
  })
}

function toBackendParams(params: { cost: string }) {
  return { Cost: (parseFloat(params.cost) * 100).toFixed(0) }
}

function toBackendConditions(
  conditions: ShippingCondition[],
  countries: Country[],
  trans: (key: string) => string,
  reasonKeys: string[],
  availableConfiguration: ShippingConfiguration
): PbCondition[] {
  const backendConditions: PbCondition[] = conditions.map((c) => {
    return {
      function: toBackendFunction(c.function),
      comparison: c.comparison,
      values: toBackendValues(
        c.function,
        c.values,
        countries,
        trans,
        reasonKeys
      ),
      variable: findCorrectVariable(c.function, availableConfiguration)
    }
  })
  return backendConditions
}

function findCorrectVariable(
  funcName: string,
  availableConfiguration: ShippingConfiguration
): string {
  return (
    availableConfiguration.functions.find((f) => f.function === funcName)
      ?.variable ?? ''
  )
}

function toBackendFunction(func: string): string {
  switch (func) {
    case 'Country':
      return 'country'
    case 'Refund method':
      return 'refundMethod'
    case 'Return reason':
      return 'returnReason'
    case 'Return method':
      return 'returnMethod'
    case 'Postal code':
      return 'postalCodePrefix'
    case 'Order comes from an exchange':
      return 'reverExchange'
    case 'Order fully returned':
      return 'orderFullyReturned'
    case 'Product Category ID':
      return 'productCategoryID'
    case 'Product Collection ID':
      return 'productCollectionID'
    case 'Product Tag ID':
      return 'productTagID'
    case 'Process Product ID':
      return 'processProductID'
    case 'Total amount':
      return 'orderTotalAmount'
    case 'Total number of products':
      return 'orderTotalNumberOfProducts'
    case 'Number of products returned':
      return 'totalProductsReturned'
    case 'Total products value':
      return 'totalProductsValue'
    case 'Refunded exchange amount':
      return 'committedExchangeAmount'
    case 'Refunded promocode amount':
      return 'committedPromocodeAmount'
    case 'Refunded gift card amount':
      return 'committedGiftAmount'
    case 'Refunded bank transfer amount':
      return 'committedBankTransferAmount'
    case 'Refunded original amount':
      return 'committedOriginalAmount'
    case 'Days since bought':
      return 'daysSinceBought'
    case 'Order Tag ID':
      return 'orderTagID'
    default:
      return func
  }
}

function toBackendValues(
  func: string,
  values: string[],
  countries: Country[],
  trans: (key: string) => string,
  reasonKeys: string[]
): string[] {
  const funcName = toFrontendFunction(func)
  switch (funcName) {
    case 'Country':
      return values.map((v) => countryNameToCountryCode(v, countries))
    case 'Refund method':
      return values.map((v) => toBackendRefundMethod(v))
    case 'Return reason':
      return values.map((v) => toBackendReturnReason(v, trans, reasonKeys))
    case 'Return method':
      return values.map((v) => toBackendReturnMethod(v))
    case 'Total amount':
    case 'Total products value':
    case 'Refunded exchange amount':
    case 'Refunded promocode amount':
    case 'Refunded gift card amount':
    case 'Refunded bank transfer amount':
    case 'Refunded original amount':
      return values.map((v) => (parseFloat(v) * 100).toFixed(0))
    default:
      return values
  }
}

function countryNameToCountryCode(name: string, countries: Country[]): string {
  if (name === EU_ALIAS_NAME) return EU_ALIAS_CODE
  const country = countries.find((country) => country.name === name)
  return country?.iso ?? name
}

function toBackendRefundMethod(method: string): string {
  switch (method) {
    case 'Bank transfer':
      return 'BANK_TRANSFER'
    case 'Original payment method':
      return 'ORIGINAL'
    case 'Promo code':
      return 'PROMO_CODE'
    case 'Gift card':
      return 'GIFT_CARD'
    case 'Exchange':
      return 'EXCHANGE'
    default:
      return method
  }
}

function toBackendReturnReason(
  reason: string,
  trans: (key: string) => string,
  reasonKeys: string[]
): string {
  if (reason === 'No reason') return 'NO_RETURN_REASON'
  const unchangedReason = reasonKeys.find(
    (key) => trans(`select_reason_${key}`) === reason
  )
  if (unchangedReason) return unchangedReason
  // A value already in wire form (the chat agent emits reason keys) must not be
  // blanked: an empty condition saves silently and then matches nothing.
  return reasonKeys.includes(reason) ? reason : ''
}

function toBackendReturnMethod(method: string): string {
  switch (method) {
    case 'Home pick-up':
      return 'HOME_PICK_UP'
    case 'Drop-off point':
      return 'DROP_OFF_POINT'
    case 'Delivery and pick-up':
      return 'DELIVERY_PICKUP'
    case 'In store':
      return 'IN_STORE'
    case 'Self shipping':
      return 'SELF_SHIPPING'
    default:
      return method
  }
}
